/**
 * Service link resolver — computes the case studies and city × service crawl
 * links most relevant to a given service page. Used by <ServiceInternalLinks />
 * so every priority service route exposes a consistent internal-link block.
 */

import { CASE_STUDIES, type CaseStudy } from "@/data/caseStudies";
import { CITY_HUBS, type CityHub } from "@/data/cityHubs";

export type ServiceCategory = CaseStudy["category"];

export interface ServiceCityCrawlLink {
  /** /case-studies?city=…&category=… deep link. */
  href: string;
  search: { city: string; category: ServiceCategory };
  /** SEO-friendly anchor text e.g. "Custom Awnings in San Antonio". */
  anchor: string;
  city: CityHub;
  /** Whether the city has at least one published case study in this category. */
  hasMatch: boolean;
}

export interface ServiceInternalLinkSet {
  category: ServiceCategory;
  /** All case studies in this category, hero/primary first. */
  studies: CaseStudy[];
  /** City × service text-link crawl matrix (one row per city hub). */
  cityLinks: ServiceCityCrawlLink[];
  /** /case-studies?category=… index for the whole category. */
  categoryHref: string;
}

/**
 * Map service path → matching case-study category. Service pages without a
 * dedicated category (e.g. /custom-fabrication) get `undefined` and the
 * component renders only the city × service crawl matrix.
 */
export const SERVICE_PATH_TO_CATEGORY: Record<string, ServiceCategory> = {
  "/awnings": "Awnings",
  "/commercial-awnings": "Commercial Awnings",
  "/sail-shades": "Sail Shades",
  "/boat-covers": "Boat Covers",
  "/pergola-covers": "Patio Covers",
  "/trailer-covers": "Stock Trailer Covers",
};

/** Singular/plural-friendly anchor builder. */
function buildAnchor(serviceLabel: string, cityName: string): string {
  return `${serviceLabel} in ${cityName}, TX`;
}

export function getServiceInternalLinks(
  category: ServiceCategory,
  serviceLabel: string = category,
): ServiceInternalLinkSet {
  const studies = CASE_STUDIES.filter((s) => s.category === category);

  const cityLinks: ServiceCityCrawlLink[] = CITY_HUBS.map((hub) => {
    const params = new URLSearchParams();
    params.set("city", hub.slug);
    params.set("category", category);
    const hasMatch = studies.some((s) => s.cityHref === `/${hub.slug}`);
    return {
      href: `/case-studies?${params.toString()}`,
      search: { city: hub.slug, category },
      anchor: buildAnchor(serviceLabel, hub.name),
      city: hub,
      hasMatch,
    };
  }).sort((a, b) => Number(b.hasMatch) - Number(a.hasMatch));

  const catParams = new URLSearchParams();
  catParams.set("category", category);

  return {
    category,
    studies,
    cityLinks,
    categoryHref: `/case-studies?${catParams.toString()}`,
  };
}
